import { useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CargaDeAtendimentoECrm } from '@/data/cadastros';
import type { Conversa } from '@/data/tipos';

export interface TransferirConversaModalProps {
    conversa: Conversa;
    filaAtual?: string;
    cadastros: CargaDeAtendimentoECrm;
    onFechar: () => void;
    onTransferida: () => void;
}

/**
 * Transferir a conversa para outra fila — e, se quiser, para alguém dela.
 *
 * Só aparece quem atende a fila escolhida: mandar para quem não está nela faria
 * a conversa sumir da lista da fila e cair na de quem não a vê.
 */
export function TransferirConversaModal({ conversa, filaAtual, cadastros, onFechar, onTransferida }: TransferirConversaModalProps) {
    const aviso = useAviso();
    const idFila = useId();
    const idPessoa = useId();
    const ativas = cadastros.filas.filter((f) => f.ativa);
    const inicial = filaAtual && ativas.some((f) => f.id === filaAtual) ? filaAtual : '';

    const [filaId, setFilaId] = useState(inicial);
    const [atendenteId, setAtendenteId] = useState('');
    const [salvando, setSalvando] = useState(false);

    const fila = ativas.find((f) => f.id === filaId);
    const pessoa = fila?.membros.find((m) => m.id === atendenteId);
    const sujo = filaId !== inicial || atendenteId !== '';

    const transferir = async () => {
        if (!fila) return;
        setSalvando(true);
        try {
            await dados().transferirConversa(conversa.id, fila.id, pessoa?.id);
            aviso({ title: 'Conversa transferida', description: `${conversa.nome} · ${fila.nome}${pessoa ? ` · ${pessoa.nome}` : ''}` });
            onTransferida();
            onFechar();
        } catch (e) {
            aviso({ title: 'Não foi possível transferir', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const nomeDoDepartamento = (id: string) => cadastros.departamentos.find((d) => d.id === id)?.nome ?? '';

    return (
        <Modal
            open width={480} title="Transferir conversa" description={conversa.nome} onClose={onFechar}
            dirty={sujo} onSave={() => { void transferir(); }} saveLabel="Transferir"
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void transferir(); }} disabled={!fila || salvando}>{salvando ? 'Transferindo…' : 'Transferir'}</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {ativas.length === 0 ? (
                    <Alert tone="warning" title="Nenhuma fila ativa">
                        Crie ou reative uma fila em Configurações › Atendimentos &amp; CRM para poder transferir.
                    </Alert>
                ) : (
                    <>
                        <FormField label="Fila" htmlFor={idFila} required>
                            <Select
                                id={idFila} value={filaId} placeholder="Escolha a fila"
                                onChange={(v) => { setFilaId(v); setAtendenteId(''); }}
                                options={ativas.map((f) => ({ value: f.id, label: `${f.nome} — ${nomeDoDepartamento(f.departamentoId)}` }))}
                            />
                        </FormField>
                        <FormField
                            label="Pessoa" htmlFor={idPessoa}
                            hint={fila && fila.membros.length === 0
                                ? 'Esta fila não tem ninguém vinculado: a conversa fica sem quem a veja até alguém entrar nela.'
                                : 'Só quem atende a fila aparece aqui. Em branco, a conversa fica com a fila.'}
                        >
                            <Select
                                id={idPessoa} value={atendenteId} placeholder="Qualquer pessoa da fila" disabled={!fila}
                                onChange={setAtendenteId}
                                options={(fila?.membros ?? []).map((m) => ({ value: m.id, label: m.nome }))}
                            />
                        </FormField>
                        {pessoa && (
                            <Alert tone="neutral" title={`A conversa passa para ${pessoa.nome}`}>
                                E sai do comando da assistente, como quando alguém assume.
                            </Alert>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
